import React, { useEffect, useState } from "react";
import useFetch from "../../../hooks/useFetch";
import { API, GST_STATES } from "../../../common/Constants";
import { getAPIHeaders, isNotEmpty } from "../../../common/Utils";
import PreLoader from "../preLoader";
import CommonSelectV2 from "../commonSelectV2";
import { BillingAddressData } from "../../data/interface";



interface BillingAddressProps {
  onAddressSaved?: (address: BillingAddressData) => void;
  showTitle?: boolean;
  className?: string;
}

const BillingAddress: React.FC<BillingAddressProps> = ({ 
  onAddressSaved, 
  showTitle = true,
  className = ""
}) => {
  const { isLoading, error, callFetch } = useFetch();
  const [addressData, setAddressData] = useState<BillingAddressData | null>(null);
  
  const [message, setMessage] = useState<{
    message: string;
    error: boolean;
  }>({
    message: '',
    error: false
  });

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  useEffect(() => {
    fetchAddress();
  }, []);

  useEffect(() => {
    if (error) {
      let errMsg = "";
      if (error.response?.data) {
        errMsg = error.response?.data;
      } else {
        errMsg = 'Something went wrong please try again later.';
      }
      setMessage({ message: errMsg, error: true });
    }
  }, [error]);

  const fetchAddress = () => {
    callFetch(
      API.ADDRESS,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((res) => {
      // Check if response is an array and if it's empty
      if (Array.isArray(res) && res.length === 0) {
        setAddressData(null);
        return;
      }
      
      if (res && res.length > 0) {
        const billingAddress = res.find((addr: any) => addr.addressTypeCode === "BILLING");
        if (billingAddress) {
          setAddressData(billingAddress);
        } else {
            setAddressData(null);
        }
      } else {
        setAddressData(null);
      }
    }).catch((err) => {
      // No existing address found, this is normal for new users
      setAddressData(null);
    });
  };

  const handleInputChange = (field: keyof BillingAddressData, value: string | number | boolean) => {
    setAddressData({
        ...addressData,
        [field]: value
      } as BillingAddressData);
  };

  const handleStateChange = (selectedStateValue: string) => {
    const selectedState = GST_STATES.find(state => state.value === selectedStateValue);
    if (selectedState) {
      if (addressData) {
        setAddressData({
          ...addressData,
          stateProvince: selectedState.label,
          gstStateCode: selectedState.value
        });
      }
    }
  };

  const validateForm = () => {
    if (!isNotEmpty(addressData?.addressLine1)) {
      setMessage({ message: "Address Line 1 is required", error: true });
      return false;
    }
    if (!isNotEmpty(addressData?.city)) {
      setMessage({ message: "City is required", error: true });
      return false;
    }
    if (!isNotEmpty(addressData?.stateProvince)) {
      setMessage({ message: "State is required", error: true });
      return false;
    }
    if (!isNotEmpty(addressData?.postalCode)) {
      setMessage({ message: "Postal Code is required", error: true });
      return false;
    }
    return true;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setMessage({ message: "", error: false });

    if (!validateForm()) {
      return;
    }

    const payload: any = {
      ...addressData,
      addressTypeCode: "BILLING",
      countryCode : "IND"
    };

    if (addressData?.id) {
      // Update existing address
      callFetch(
        `${API.ADDRESS}`,
        'PUT',
        getAPIHeaders(),
        null,
        payload
      ).then(()=>{
        setIsEditMode(false);
        fetchAddress()
        onAddressSaved?.(payload);
      })
      .catch((err) => {});
    } else {
      // Create new address
      callFetch(
        API.ADDRESS,
        'POST',
        getAPIHeaders(),
        null,
        payload
      ).then(()=>{
        setIsEditMode(false);
        fetchAddress()
        onAddressSaved?.(payload);
      })
      .catch((err) => {});
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    if (addressData) {
      // If we have existing address data, reset to it
      fetchAddress();
    } else {
      // If no existing address, reset to null
      setAddressData(null);
    }
    setIsEditMode(false);
    setMessage({ message: "", error: false });
  };

  return (
    <div className={`billing-address-component ${className}`}>
      <PreLoader showLoader={isLoading} />
      
      {showTitle && (
        <div className="border-bottom mb-3">
          <h6 className="mb-3">Billing Address</h6>
        </div>
      )}

      {addressData && !isEditMode ? (
        // Display existing address
        <div className="border-bottom mb-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Current Billing Address</h6>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handleEdit}
            >
              Edit Address
            </button>
          </div>
          <div className="address-display">
            <p className="mb-1"><strong>Address:</strong> {addressData?.addressLine1}</p>
            {addressData?.addressLine2 && (
              <p className="mb-1">{addressData?.addressLine2}</p>
            )}
            {addressData?.landmark && (
              <p className="mb-1"><strong>Landmark:</strong> {addressData?.landmark}</p>
            )}
            <p className="mb-1">
              <strong>City:</strong> {addressData?.city}, 
              <strong> State:</strong> {addressData?.stateProvince}, 
              <strong> Postal Code:</strong> {addressData?.postalCode}
            </p>
            <p className="mb-0"><strong>Country:</strong> {addressData?.countryCode}</p>
          </div>
        </div>
      ) : !addressData && isEditMode === false ? (
        // No address found - show message
        <div className="border-bottom mb-3">
          <div className="text-center p-4">
            <i className="ti ti-map-pin text-muted mb-3" style={{ fontSize: '3rem' }}></i>
            <h6 className="text-muted mb-2">Add an address</h6>
            <p className="text-muted small mb-3">You haven't added a billing address yet.</p>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleEdit}
            >
              <i className="ti ti-plus me-1"></i>
              Add Address
            </button>
          </div>
        </div>
      ) : (
        // Address form
        <form onSubmit={handleSubmit}>
          <div className="border-bottom mb-3">
            <div className="row">
              <div className="col-md-12">
                <div className="row align-items-center mb-3">
                  <div className="col-md-2">
                    <label className="form-label mb-md-0">Address Line 1 *</label>
                  </div>
                  <div className="col-md-10">
                    <input
                      type="text"
                      className="form-control"
                      value={addressData?.addressLine1}
                      onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                      placeholder="Enter address line 1"
                    />
                  </div>
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="row align-items-center mb-3">
                  <div className="col-md-4">
                    <label className="form-label mb-md-0">Address Line 2</label>
                  </div>
                  <div className="col-md-8">
                    <input
                      type="text"
                      className="form-control"
                      value={addressData?.addressLine2}
                      onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                      placeholder="Enter address line 2"
                    />
                  </div>
                </div>
              </div>
              
              
              <div className="col-md-6">
                <div className="row align-items-center mb-3">
                  <div className="col-md-4">
                    <label className="form-label mb-md-0">Landmark</label>
                  </div>
                  <div className="col-md-8">
                    <input
                      type="text"
                      className="form-control"
                      value={addressData?.landmark}
                      onChange={(e) => handleInputChange('landmark', e.target.value)}
                      placeholder="Enter landmark"
                    />
                  </div>
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="row align-items-center mb-3">
                  <div className="col-md-4">
                    <label className="form-label mb-md-0">City *</label>
                  </div>
                  <div className="col-md-8">
                    <input
                      type="text"
                      className="form-control"
                      value={addressData?.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="Enter city"
                    />
                  </div>
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="row align-items-center mb-3">
                  <div className="col-md-4">
                    <label className="form-label mb-md-0">State *</label>
                  </div>
                  <div className="col-md-8">
                    <CommonSelectV2
                      options={GST_STATES}
                      value={addressData?.gstStateCode ? GST_STATES.find(state => state.value === addressData?.gstStateCode) : null}
                      onChange={handleStateChange}
                      placeHolder="Select State"
                      className="react-select-container"
                    />
                  </div>
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="row align-items-center mb-3">
                  <div className="col-md-4">
                    <label className="form-label mb-md-0">Postal Code *</label>
                  </div>
                  <div className="col-md-8">
                    <input
                      type="text"
                      className="form-control"
                      value={addressData?.postalCode}
                      onChange={(e) => handleInputChange('postalCode', e.target.value)}
                      placeholder="Enter postal code"
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {message.message && (
            <div className={`mb-3 ${message.error ? 'text-danger' : 'text-success'}`}>
              <i className={`ti ${message.error ? 'ti-alert-triangle' : 'ti-thumb-up'}`} /> 
              {message.message}
            </div>
          )}

          <div className="d-flex align-items-center justify-content-end">
            <button
              type="button"
              className="btn btn-outline-light border me-3"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {addressData ? 'Update Address' : 'Save Address'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default BillingAddress;
